import type { Jsonified } from "@redotech/json/json";
import * as zExt from "@redotech/rpc/ext";
import { isEnumValue } from "@redotech/util/enum";
import { createEnum } from "@redotech/zod/zod-enum";
import type { ObjectId } from "bson";
import * as z from "zod/v4";
import { CheckoutAbTestMetricToOptimize } from "./checkouts/checkout-ab-test-builder";
import { progressBarSchema } from "./checkouts/checkout-optimization-settings";
import { CheckoutTreeSchema } from "./checkouts/checkout-tree";
import { ConversationPlatform } from "./conversation";
import { checkoutAnalyticsVersionEnum } from "./conversion/checkout-conversion-metrics/analytics";
import { customVisualSchema } from "./conversion/custom-checkout-split-metrics/custom-visual";
import type { CoverageMlModelDb } from "./coverage-calculator/coverage-ml/registry";
import {
  ExtendedWarrantyBlurbType,
  ExtendedWarrantyDefaultSelection,
  ExtendedWarrantyPdpExperience,
  ExtendedWarrantyPdpNoCoverageType,
  ExtendedWarrantyRadioOptionType,
  ExtendedWarrantyTilesSize,
} from "./extended-warranty";
import {
  ExtendedWarrantyCartToggleExperience,
  ExtendedWarrantyPdpRenderMethod,
  ExtendedWarrantyPopupExperience,
} from "./extended-warranty-enable-criteria";
import { CheckoutExperienceZodArraySchema } from "./integration/redo-in-shipping/ris";
import type { AttachmentStrategy } from "./returns/attachment-strategy";
import { Icons } from "./storefront/checkout-ui-customization";
import type {
  CoverageCreditIncentive,
  DynamicReturnPricing,
  InternationalReturnPricing,
  PricingRuleSet,
  ToggleFields,
} from "./team";
import {
  ClickStandardCheckoutButton,
  InsertionPosition,
  TermsAndConditions,
  WidgetConfig,
} from "./widget";

/**
 * TODO - This should probably be called `SplitType`
 */
export enum TreatmentType {
  COVERAGE = "Treatment",
  CONCIERGE = "ConciergeTreatment",
  CHECKOUT_OPTIMIZATION = "CheckoutOptimization",
  EXPANDED_WARRANTY = "ExpandedWarranty",
  SIGNUP_FORM = "SignupForm",
  RESCUE_PANEL = "RescuePanel",
  SUPPORT_AGENT = "SupportAgent",
  PRICING_TEST = "PricingTest",
  MARKETING_AGENT = "MarketingAgent",
  SPOTLIGHT = "Spotlight",
  AUTOMATION_OUTREACH = "AutomationOutreach",
}

/**
 * These are the treatment types for split tests run in Redo admin app.
 * E.g. CHECKOUT_OPTIMIZATION split tests are run by merchants directly from
 * merchant app.
 */
// TODO: Refactor to use a Record<TreatmentType, string>
export const AdminTreatmentTypes = [
  TreatmentType.COVERAGE,
  TreatmentType.CONCIERGE,
  TreatmentType.EXPANDED_WARRANTY,
] as const;
export type AdminTreatmentType = (typeof AdminTreatmentTypes)[number];
export function isAdminTreatmentType(
  treatmentType: string,
): treatmentType is AdminTreatmentType {
  return AdminTreatmentTypes.includes(treatmentType as AdminTreatmentType);
}
export enum MetricName {
  PAGE_VIEWED = "page_viewed",
  PRODUCT_ADDED_TO_CART = "product_added_to_cart",
  CART_VIEWED = "cart_viewed",
  CHECKOUT_STARTED = "checkout_started",
  CHECKOUT_COMPLETED = "checkout_completed",
  TOTAL_SPENT = "total_spent",
  TOTAL_MERCHANT_ITEMS_PURCHASED = "total_merchant_items_purchased",
  RETURN_PURCHASED = "return_purchased",
  PACKAGE_PROTECTION_PURCHASED = "package_protection_purchased",
  RETURN_PACKAGE_PROTECTION_PURCHASED = "return,package_protection_purchased",
  EW_ELIGIBLE_PRODUCT_PURCHASED = "ew_eligible_product_purchased",
  EW_PURCHASED = "ew_purchased",
  SHIPPING_REVENUE = "shipping_revenue",
  SIGNUP_FORM_VIEWED = "signup_form_viewed",
  SIGNUP_FORM_SUBMITTED = "signup_form_submitted",
  RESCUE_PANEL_TRIGGERED = "rescue_panel_triggered",
  RESCUE_PANEL_CTA_CLICKED = "rescue_panel_cta_clicked",
  SUPPORT_CONVERSATION_STARTED = "support_conversation_started",
  SUPPORT_CONVERSATION_RESOLVED = "support_conversation_resolved",
  SUPPORT_CONVERSATION_ESCALATED = "support_conversation_escalated",
  SUPPORT_CUSTOMER_RESPONDED = "support_customer_responded",
  SUPPORT_RESOLUTION_REVENUE_GENERATED = "support_resolution_revenue_generated",
  SUPPORT_CONVERSATION_MESSAGE_COUNT = "support_conversation_message_count",
  SUPPORT_CUSTOMER_MESSAGE_COUNT = "support_customer_message_count",
  SUPPORT_PRODUCTS_ADDED_TO_CART_COUNT = "support_products_added_to_cart_count",
  SUPPORT_CART_VALUE_ADDED_CENTS = "support_cart_value_added_cents",
  SUPPORT_ORDERS_PRODUCED_COUNT = "support_orders_produced_count",
  SUPPORT_TOTAL_COST = "support_total_cost",
}

export function isMetricName(name: string): name is MetricName {
  return isEnumValue(name, MetricName);
}

export type Treatment = Jsonified<TreatmentDoc>;

export type GenericTreatment = {
  _id: ObjectId;
  split_id: ObjectId;
  name: string;
  active: boolean;
  settings: object;
  weight: number;
};

export interface FinalSaleReturnsPricing {
  minPrice: number;
  maxPrice: number;
  percentage: number;
  shippingLabelCoveragePrice?: number;
  pricingRuleSet: PricingRuleSet[];
}

export interface TreatmentDoc {
  _id: ObjectId;
  split_id: ObjectId;
  name: string;
  active: boolean;
  settings: {
    redoEnabled: boolean;
    returnEnabled: boolean;
    packageProtectionEnabled: boolean;
    attachmentStrategy: AttachmentStrategy;
    toggleTextOptions?: {
      returnToggle: ToggleFields;
      packageProtectionToggle: ToggleFields;
      bothProductToggle: ToggleFields;
    };
    hideOnPDP: boolean;
    redoAutoEnable: boolean;
    packageProtectionAutoEnable: boolean;
    hideBranding: boolean;
    hideCartProduct?: boolean;
    hideCartProductSelector?: string;
    hideCheckoutButtonSelector?: string;
    showIcons?: boolean;
    cartElementPlacementSelector?: string;
    cartElementShadowRootPlacementSelector?: string;
    cartListenerSelector?: string;
    cartElementPlacement?: InsertionPosition;
    cartElementImage?: URL;
    usingCartAndCheckoutToggle?: boolean;
    forceDoubleCheckoutToggle?: boolean;
    hideCheckoutUiWhenOptedIn?: boolean;
    customToggleCss?: string;
    displayIcon?: boolean;
    icon?: Icons;
    iconColor?: string;
    iconBackgroundColor?: string;
    returnOptions?: {
      pricePerOrder: string;
      serviceCountries: string[];
      dynamicPricing?: DynamicReturnPricing;
      internationalPricing?: InternationalReturnPricing;
      excludeCoverageIfAnyInvalidProduct: boolean;
    };
    widgetConfigs?: WidgetConfig[];
    groupNearbyToggleElementsTogether?: boolean;
    packageProtectionPricingRulesets?: PricingRuleSet[];
    packageProtectionMinPrice?: string;
    packageProtectionMaxPrice?: string;
    finalSaleReturnsEnabled?: boolean;
    finalSaleReturnsPricing?: FinalSaleReturnsPricing;
    splitProducts?: boolean;
    termsAndConditions?: TermsAndConditions;
    clickStandardCheckoutButton?: ClickStandardCheckoutButton;
    coverageCreditIncentive?: CoverageCreditIncentive;
    lineItemRemoveOverrideSettings?: {
      enabled: boolean;
      showOnAllItems: boolean;
      buttonText: string;
    };
    packageProtectionCoverageMlModel?: CoverageMlModelDb;
  };
  weight: number;
}

export type TestMetricsTimeseries = Partial<{
  [metricWithSource in string]: (number | null)[];
}> & { date: string[] };

export interface TreatmentPerformance {
  treatmentId: string;
  treatmentName: string;
  views: number;
  checkouts: number;
  conversionRate: number;
  returnsAdoption: number;
  averageOrderPrice: number;
  spendPerVisitor: number;
  rawData: TestMetricsTimeseries;
  expandedWarrantyAdoption: number;
}

export interface PopulatedSplit {
  _id: string;
  name?: string;
  team: string;
  active: boolean;
  treatments: Treatment[];
  startDate: string;
  endDate?: string | null;
  deleted?: boolean;
  treatmentType: AdminTreatmentType;
}

export type Split = Jsonified<SplitDoc>;

export const baseSplitZodSchema = z.object({
  _id: zExt.objectId(),
  name: z.string().optional(),
  team: zExt.objectId(),
  active: z.boolean().optional(),
  treatments: z.array(zExt.objectId()),
  startDate: z.date().optional(),
  endDate: z.date().nullable().optional(),
  deleted: z.boolean().optional(),
  treatmentType: createEnum(TreatmentType, { enumName: "TreatmentType" }),
});

export type BaseSplitZodType = z.infer<typeof baseSplitZodSchema>;

export const checkoutOptimizationSplitZodSchema = baseSplitZodSchema.extend({
  name: z.string(),
  description: z.string(),
  metricToOptimize: createEnum(CheckoutAbTestMetricToOptimize, {
    enumName: "CheckoutAbTestMetricToOptimize",
  }),
  controlTreatment: zExt.objectId(),
  createdAt: z.date(),
  concluded: z
    .object({
      selectedTreatmentId: zExt.objectId(),
      checkoutTreeSnapshot: CheckoutTreeSchema.optional(),
      checkoutExperiences: CheckoutExperienceZodArraySchema.optional(),
    })
    .optional(),
  scheduled: z
    .object({ prospectiveCheckoutTree: CheckoutTreeSchema.optional() })
    .optional(),
  aiAnalysis: z
    .object({ analysis: z.string(), lastUpdatedAt: z.date() })
    .optional(),
  customVisuals: z.array(customVisualSchema).optional(),
  analyticsVersionOverride: createEnum(checkoutAnalyticsVersionEnum, {
    enumName: "CheckoutAnalyticsVersion",
  }).optional(),
  tags: z.array(z.string()).optional(),
});
export type CheckoutOptimizationSplitType = z.infer<
  typeof checkoutOptimizationSplitZodSchema
>;

export const rescuePanelSplitZodSchema = baseSplitZodSchema.extend({
  name: z.string(),
  createdAt: z.date(),
  merchantDashboardVisible: z.boolean(),
});
export type RescuePanelSplitType = z.infer<typeof rescuePanelSplitZodSchema>;

export const supportAgentSplitZodSchema = baseSplitZodSchema.extend({
  name: z.string(),
  description: z.string().optional(),
  createdAt: z.date(),
});
export type SupportAgentSplitType = z.infer<typeof supportAgentSplitZodSchema>;

export const marketingAgentSplitZodSchema = baseSplitZodSchema.extend({
  discountId: zExt.objectId().optional(),
  cooldownHours: z.number().positive(),
});
export type MarketingAgentSplitType = z.infer<
  typeof marketingAgentSplitZodSchema
>;

export interface SplitDoc {
  _id: ObjectId;
  name?: string;
  team: ObjectId;
  active: boolean;
  treatments: ObjectId[];
  startDate: Date;
  endDate?: Date | null;
  deleted?: boolean;
}

export enum DataSource {
  PIXEL = "pixel",
  ORDER = "order",
  CARRIER_SERVICE = "carrier_service",
  STOREFRONT_EXTENSION = "storefront_extension",
}

export interface TestMetricDoc {
  treatment_id: ObjectId;
  metric: MetricName;
  date: string;
  dataSource: DataSource;
  country: string;
  priceBracket: number;
  numMerchantItemsPurchased: number;
  value: number;
  order_id?: ObjectId;
}

export const checkoutOptimizationTreatmentStorefrontFeaturesSchema = z.object({
  progressBar: progressBarSchema.optional(),
  textEdits: z.array(
    z.object({
      selector: z.string(),
      textToReplace: z.string(),
      replacementText: z.string(),
    }),
  ),
});
export type StorefrontFeatures = z.infer<
  typeof checkoutOptimizationTreatmentStorefrontFeaturesSchema
>;

export const checkoutOptimizationTreatmentZodSchema = z.object({
  _id: zExt.objectId(),
  splitId: zExt.objectId(),
  weight: z.number(),
  name: z.string(),
  storefrontFeatures:
    checkoutOptimizationTreatmentStorefrontFeaturesSchema.optional(),
});
export type CheckoutOptimizationTreatment = z.infer<
  typeof checkoutOptimizationTreatmentZodSchema
>;

export const rescuePanelTreatmentZodSchema = z.object({
  _id: zExt.objectId(),
  splitId: zExt.objectId(),
  weight: z.number(),
  name: z.string(),
  rescuePanelConfig: z.object({
    enabled: z.boolean(),
    dropoffPercentageTrigger: z.number().min(0).max(100).optional(),
    secondsInColdTrigger: z.number().optional(),
  }),
});
export type RescuePanelTreatment = z.infer<
  typeof rescuePanelTreatmentZodSchema
>;

const modelTreatmentSchema = z.object({
  kind: z.literal("modelTreatment"),
  model: z.enum([
    "gpt-4.1",
    "gpt-5",
    "gpt-5.1",
    "gpt-5.2",
    "gpt-4.1-mini",
    "gpt-5-mini",
    "gpt-5-nano",
  ]),
});

const temperatureTreatmentSchema = z.object({
  kind: z.literal("temperatureTreatment"),
  temperature: z.number(),
});

const supportAgentTreatmentConfigSchema = z.discriminatedUnion("kind", [
  modelTreatmentSchema,
  temperatureTreatmentSchema,
]);

export type TreatmentUnionType = z.infer<
  typeof supportAgentTreatmentConfigSchema
>;

export const supportAgentTreatmentZodSchema = z.object({
  _id: zExt.objectId(),
  splitId: zExt.objectId(),
  weight: z.number(),
  name: z.string(),
  channel: createEnum(ConversationPlatform, {
    enumName: "ConversationPlatform",
  }).optional(),
  supportAgentConfig: supportAgentTreatmentConfigSchema,
});
export type SupportAgentTreatment = z.infer<
  typeof supportAgentTreatmentZodSchema
>;

export const expandedWarrantyTreatmentZodSchema = z.object({
  _id: zExt.objectId(),
  type: z.literal(TreatmentType.EXPANDED_WARRANTY),
  split_id: zExt.objectId(),
  name: z.string(),
  weight: z.number(),
  active: z.boolean(),
  settings: z.object({ expandedWarrantiesEnabled: z.boolean().optional() }),
  ewWidgetConfig: z.object({
    variantSelector: z.string().optional(),
    customToggleCss: z.string().optional(),
    cartToggleExperience: createEnum(ExtendedWarrantyCartToggleExperience, {
      enumName: "ExtendedWarrantyCartToggleExperience",
    }).optional(),
    cartToggleModalNotAddedDescription: z.string().optional(),
    cartToggleModalAddedDescription: z.string().optional(),
    cartToggleAddedDescription: z.string().optional(),
    cartToggleNotAddedDescription: z.string().optional(),
    clickListenerSelector: z.string().optional(),
    customPdpCss: z.string().optional(),
    pdpExperience: createEnum(ExtendedWarrantyPdpExperience, {
      enumName: "ExtendedWarrantyPdpExperience",
    }).optional(),
    pdpDefaultSelection: createEnum(ExtendedWarrantyDefaultSelection, {
      enumName: "ExtendedWarrantyDefaultSelection",
    }).optional(),
    pdpRenderMethod: createEnum(ExtendedWarrantyPdpRenderMethod, {
      enumName: "ExtendedWarrantyPdpRenderMethod",
    }).optional(),
    pdpNoCoverageType: createEnum(ExtendedWarrantyPdpNoCoverageType, {
      enumName: "ExtendedWarrantyPdpNoCoverageType",
    }).optional(),
    pdpNoCoveragePrice: z.string().optional(),
    pdpSelector: z.string().optional(),
    pdpTitle: z.string().optional(),
    pdpLabel: z.string().optional(),
    pdpCompactBlurbTitle: z.string().optional(),
    pdpTilesSize: createEnum(ExtendedWarrantyTilesSize, {
      enumName: "ExtendedWarrantyTilesSize",
    }).optional(),
    pdpBlurbType: createEnum(ExtendedWarrantyBlurbType, {
      enumName: "ExtendedWarrantyBlurbType",
    }).optional(),
    pdpCheckboxTitle: z.string().optional(),
    pdpRadioOptionType: createEnum(ExtendedWarrantyRadioOptionType, {
      enumName: "ExtendedWarrantyRadioOptionType",
    }).optional(),
    pdpRadioOptionTitle: z.string().optional(),
    pdpCoverageDisabledLabel: z.string().optional(),
    pdpDescription: z.string().optional(),
    pdpAddToCartDescription: z.string().optional(),
    pdpAddedToCartTitle: z.string().optional(),
    pdpAddedToCartDescription: z.string().optional(),
    pdpTilesBackdropEnabled: z.boolean().optional(),
    pdpInsertPosition: createEnum(InsertionPosition, {
      enumName: "InsertionPosition",
    }).optional(),
    customModalCss: z.string().optional(),
    logo: z.string().nullable().optional(),
    sideImage: z.string().nullable().optional(),
    modalTitle: z.string().optional(),
    modalCoverageHeader: z.string().optional(),
    modalDescription: z.string().optional(),
    modalPlanHeader: z.string().optional(),
    modalBulletPoints: z.array(z.string()).optional(),
    modalSeeDetailsUrl: z.string().optional(),
    popupExperience: createEnum(ExtendedWarrantyPopupExperience, {
      enumName: "ExtendedWarrantyPopupExperience",
    }).optional(),
    popupClickListenerSelector: z.string().optional(),
    popupFoundSelector: z.string().optional(),
    popupTitle: z.string().optional(),
    popupTitleSubtext: z.string().optional(),
    popupDescription: z.string().optional(),
    popupButtonAccept: z.string().optional(),
    popupButtonDecline: z.string().optional(),
    customPopupCss: z.string().optional(),
  }),
});

export type ExpandedWarrantyTreatment = z.infer<
  typeof expandedWarrantyTreatmentZodSchema
>;

export const signupFormSplitZodSchema = baseSplitZodSchema.extend({
  name: z.string(),
});
export type SignupFormSplitType = z.infer<typeof signupFormSplitZodSchema>;

export const signupFormTreatmentZodSchema = z.object({
  _id: zExt.objectId(),
  split_id: zExt.objectId(),
  name: z.string(),
  weight: z.number(),
  active: z.boolean(),
  formId: zExt.objectId(),
});

export type SignupFormTreatment = z.infer<typeof signupFormTreatmentZodSchema>;

const variantSearchResultSchema_ = z.object({
  variantId: z.string(),
  productId: z.string(),
  productTitle: z.string(),
  variantTitle: z.string(),
  name: z.string(),
  imageSrc: z.string(),
  price: z.string(),
});
export const variantSearchResultSchema: z.ZodType<VariantSearchResult> =
  variantSearchResultSchema_;
export interface VariantSearchResult
  extends z.infer<typeof variantSearchResultSchema_> {}

export const pricingTestVariantPriceSchema = z.object({
  variantId: z.string(),
  productId: z.string(),
  productTitle: z.string(),
  variantTitle: z.string(),
  price: z.number(),
});
export type PricingTestVariantPrice = z.infer<
  typeof pricingTestVariantPriceSchema
>;

export const pricingTestTreatmentConfigSchema = z.object({
  variantPrices: z.array(pricingTestVariantPriceSchema),
});
export type PricingTestTreatmentConfig = z.infer<
  typeof pricingTestTreatmentConfigSchema
>;

export const pricingTestTreatmentZodSchema = z.object({
  _id: zExt.objectId(),
  splitId: zExt.objectId(),
  weight: z.number(),
  name: z.string(),
  pricingConfig: pricingTestTreatmentConfigSchema,
});
export type PricingTestTreatment = z.infer<
  typeof pricingTestTreatmentZodSchema
>;

export const pricingTestSplitZodSchema = baseSplitZodSchema.extend({
  name: z.string(),
  createdAt: z.date(),
});
export type PricingTestSplitType = z.infer<typeof pricingTestSplitZodSchema>;

export const automationOutreachStepTypes = [
  "send_email",
  "send_sms",
  "recover",
] as const;
export type AutomationOutreachStepType =
  (typeof automationOutreachStepTypes)[number];

const automationOutreachSplitZodSchema_ = baseSplitZodSchema.extend({
  name: z.string(),
  description: z.string().optional(),
  createdAt: z.date(),
  automationId: zExt.objectId(),
  automationStepId: zExt.objectId(),
  stepType: z.enum(automationOutreachStepTypes),
  originalTemplateId: zExt.objectId().optional(),
});
export const automationOutreachSplitZodSchema: z.ZodType<AutomationOutreachSplitType> =
  automationOutreachSplitZodSchema_;
export interface AutomationOutreachSplitType
  extends z.infer<typeof automationOutreachSplitZodSchema_> {}

const automationOutreachTreatmentZodSchema_ = z.object({
  _id: zExt.objectId(),
  splitId: zExt.objectId(),
  templateName: z.string(),
  buckets: z.array(z.number().min(0).max(999)),
  isControl: z.boolean().default(false),
  templateId: zExt.objectId().optional(),
});
export const automationOutreachTreatmentZodSchema: z.ZodType<AutomationOutreachTreatment> =
  automationOutreachTreatmentZodSchema_;
export interface AutomationOutreachTreatment
  extends z.infer<typeof automationOutreachTreatmentZodSchema_> {}

export const treatmentZodSchema = z.discriminatedUnion("type", [
  expandedWarrantyTreatmentZodSchema,
]);

export type TreatmentUnion = z.infer<typeof treatmentZodSchema>;

/**
 * Copied from frontend, from what I can tell these are the meanings of the parameters:
 * @param n1 - number of views for "treatment"
 * @param c1 - conversion rate for "treatment"
 * @param n2 - number of views for "control"
 * @param c2 - conversion rate for "control"
 */
export const calculateStatisticalConfidence = (
  n1: number,
  c1: number,
  n2: number,
  c2: number,
) => {
  const normalDistribution = (x: number) => {
    // Calculate the probability of a value being less than x given a normal distribution with mean 0 and standard deviation 1
    return 0.5 * (1 + erf(x / Math.sqrt(2)));
  };

  const erf = (x: number) => {
    // Error function approximation for a normal distribution
    // https://en.wikipedia.org/wiki/Error_function
    const a1 = 0.254_829_592;
    const a2 = -0.284_496_736;
    const a3 = 1.421_413_741;
    const a4 = -1.453_152_027;
    const a5 = 1.061_405_429;
    const p = 0.327_591_1;

    const sign = x >= 0 ? 1 : -1;
    const t = 1 / (1 + p * Math.abs(x));
    const y =
      1 - ((((a5 * t + a4) * t + a3) * t + a2) * t + a1) * t * Math.exp(-x * x);

    return sign * y;
  };

  const stdError1 = Math.sqrt((c1 * (1 - c1)) / n1);
  const stdError2 = Math.sqrt((c2 * (1 - c2)) / n2);
  const zScore = (c1 - c2) / Math.hypot(stdError1, stdError2);
  const p = normalDistribution(zScore);

  return p;
};
